/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Check, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import type { PendingChange, ProtectedAttentionItem } from '../../api/areas/services';
import { Badge, type Tone } from '../../components/Badge';
import { DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ReasonAction } from '../operator/shared';
import { useEnum } from './shared';
import { LIFECYCLE, StatusBadge } from '../domains/status';

const STATUS_TONE: Record<string, Tone> = { PENDING: 'pending', CONFIRMED: 'info', EXECUTED: 'ok', REJECTED: 'neutral', FAILED: 'crit' };

/** Confirm / reject buttons of one pending change (four-eyes: never the requester). */
export function PendingChangeActions({ change, onChanged }: { change: PendingChange; onChanged: () => void }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  if (change.status !== 'PENDING') return null;
  return (
    <span className="inline-flex flex-wrap gap-2">
      <ReasonAction
        label={t('protected.confirm')}
        icon={Check}
        variant="default"
        title={`${t('protected.confirmTitle')} · ${e('changeType', change.change_type)}`}
        warning={t('protected.confirmWarning')}
        run={(reason, opts) => api.confirmPendingChange(change.id, { reason }, opts)}
        done={(r) => t('protected.executed', { status: e('pendingStatus', r.status) })}
        onChanged={onChanged}
      />
      <ReasonAction
        label={t('protected.reject')}
        icon={X}
        danger
        title={`${t('protected.rejectTitle')} · ${e('changeType', change.change_type)}`}
        run={(reason, opts) => api.rejectPendingChange(change.id, { reason }, opts)}
        done={() => t('protected.rejected')}
        onChanged={onChanged}
      />
    </span>
  );
}

export function PendingChangesTable({ rows, onChanged, showDomain = true }: { rows: PendingChange[]; onChanged: () => void; showDomain?: boolean }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const cols: Column<PendingChange>[] = [
    ...(showDomain
      ? [
          {
            key: 'domain',
            header: t('protected.col.domain'),
            cell: (p: PendingChange) =>
              p.domain ? (
                <Link to={`/domains/${encodeURIComponent(p.domain)}/services`} className="font-medium text-primary hover:underline">
                  {p.domain}
                </Link>
              ) : (
                <span className="font-mono text-xs">{p.domain_id}</span>
              ),
          },
        ]
      : []),
    { key: 'change', header: t('protected.col.change'), cell: (p) => e('changeType', p.change_type) },
    { key: 'by', header: t('protected.col.requested'), cell: (p) => <span className="text-xs">{p.requested_by_type}</span> },
    { key: 'at', header: t('protected.col.at'), className: 'whitespace-nowrap text-xs', cell: (p) => formatDateTime(p.requested_at) },
    { key: 'status', header: t('protected.col.status'), cell: (p) => <Badge tone={STATUS_TONE[p.status] ?? 'neutral'}>{e('pendingStatus', p.status)}</Badge> },
    { key: 'actions', header: t('protected.col.actions'), cell: (p) => <PendingChangeActions change={p} onChanged={onChanged} /> },
  ];
  return <DataTable columns={cols} rows={rows} rowKey={(p) => p.id} empty={<EmptyState title={t('protected.empty')} />} />;
}

/** `/compliance/protected`: "Tên miền bảo vệ cần xử lý". */
export function ProtectedPage() {
  const { t } = useTranslation('services');
  const e = useEnum();
  useDocumentTitle(t('protected.title'));
  const attention = useApi((signal) => api.getProtectedAttention({ signal }), []);
  const pending = useApi((signal) => api.listPendingChanges({ status: 'PENDING' }, { signal }), []);
  const reload = () => {
    attention.reload();
    pending.reload();
  };
  const cols: Column<ProtectedAttentionItem>[] = [
    {
      key: 'domain',
      header: t('protected.col.domain'),
      cell: (i) =>
        i.domain ? (
          <Link to={`/domains/${encodeURIComponent(i.domain)}/services`} className="font-medium text-primary hover:underline">
            {i.domain}
          </Link>
        ) : (
          t('common.none')
        ),
    },
    { key: 'kind', header: t('protected.col.kind'), cell: (i) => e('attentionKind', i.kind) },
    { key: 'summary', header: t('protected.col.summary'), cell: (i) => <span className="text-xs">{i.summary}</span> },
    { key: 'state', header: t('protected.col.state'), cell: (i) => (i.lifecycle_state ? <StatusBadge map={LIFECYCLE} value={i.lifecycle_state} /> : t('common.none')) },
    { key: 'expires', header: t('protected.col.expires'), className: 'whitespace-nowrap text-xs', cell: (i) => formatDateTime(i.expires_at) },
    {
      key: 'opened',
      header: t('protected.col.opened'),
      className: 'whitespace-nowrap text-xs',
      cell: (i) => (
        <Link to={`/compliance/work/${i.item_id}`} className="text-primary hover:underline">
          {formatDateTime(i.opened_at)}
        </Link>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title={t('protected.title')}
        crumbs={[{ label: t('crumbs.compliance') }]}
        subtitle={t('protected.subtitle')}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      {attention.status === 'error' ? <DataError error={attention.error} title={t('common.loadFailed')} onRetry={reload} /> : null}
      {attention.status === 'loading' ? <Loading rows={4} /> : null}
      {attention.status === 'success' ? (
        <div className="flex flex-col gap-6">
          <StatGrid>
            <StatCard label={t('protected.protectedCount')} value={formatCount(attention.data.protected_domains)} tone="info" />
            <StatCard label={t('protected.openItems')} value={formatCount(attention.data.items.length)} tone={attention.data.items.length ? 'warn' : 'ok'} />
            <StatCard label={t('protected.pendingCount')} value={formatCount(pending.status === 'success' ? pending.data.items.length : 0)} tone="pending" />
          </StatGrid>
          <Section title={t('protected.pending')} flush>
            {pending.status === 'success' ? <PendingChangesTable rows={pending.data.items} onChanged={reload} /> : <Loading rows={2} />}
          </Section>
          <Section title={t('protected.attention')} flush>
            <DataTable columns={cols} rows={attention.data.items} rowKey={(i) => i.item_id} empty={<EmptyState title={t('protected.empty')} />} />
          </Section>
        </div>
      ) : null}
    </>
  );
}
